import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Bell, 
  Mail, 
  Smartphone, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Settings,
  Users
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AlertRule {
  id: string;
  name: string;
  type: 'budget' | 'usage' | 'performance' | 'error';
  condition: string;
  threshold: number;
  enabled: boolean;
  channels: string[];
  frequency: string;
  lastTriggered?: string;
}

interface NotificationChannel {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'webhook';
  destination: string;
  enabled: boolean;
}

export default function UsageAlerts() {
  const { toast } = useToast();
  
  const [alertRules, setAlertRules] = useState<AlertRule[]>([
    {
      id: '1',
      name: 'Daily Budget Alert',
      type: 'budget',
      condition: 'daily_spend_exceeds',
      threshold: 80,
      enabled: true,
      channels: ['email-admin'],
      frequency: 'immediate',
      lastTriggered: '2 hours ago'
    },
    {
      id: '2',
      name: 'High Usage Alert',
      type: 'usage',
      condition: 'requests_per_hour_exceeds',
      threshold: 100,
      enabled: true,
      channels: ['email-admin', 'webhook-slack'],
      frequency: 'every_hour'
    },
    {
      id: '3',
      name: 'API Error Rate Alert',
      type: 'error',
      condition: 'error_rate_exceeds',
      threshold: 5,
      enabled: false,
      channels: ['email-admin'],
      frequency: 'immediate'
    }
  ]);

  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([
    {
      id: 'email-admin',
      name: 'Admin Email',
      type: 'email',
      destination: 'admin@example.com',
      enabled: true
    },
    {
      id: 'webhook-slack',
      name: 'Slack Webhook',
      type: 'webhook',
      destination: 'https://hooks.slack.com/...',
      enabled: true
    }
  ]);

  const [showAddRule, setShowAddRule] = useState(false);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    type: 'budget' as const,
    condition: '',
    threshold: 0,
    channels: [] as string[],
    frequency: 'immediate'
  });
  const [newChannel, setNewChannel] = useState({
    name: '',
    type: 'email' as const,
    destination: ''
  });

  // Mock recent alerts
  const recentAlerts = [
    {
      id: '1',
      type: 'budget',
      message: 'Daily budget has exceeded 80% threshold',
      time: '2 hours ago',
      severity: 'warning'
    },
    {
      id: '2',
      type: 'usage',
      message: 'High API usage detected - 150 requests in last hour',
      time: '4 hours ago',
      severity: 'info'
    },
    {
      id: '3',
      type: 'error',
      message: 'API error rate increased to 3.2%',
      time: '1 day ago',
      severity: 'error'
    }
  ];

  const toggleRule = (ruleId: string) => {
    setAlertRules(prev => prev.map(rule => 
      rule.id === ruleId 
        ? { ...rule, enabled: !rule.enabled }
        : rule
    ));
  };

  const toggleChannel = (channelId: string) => {
    setNotificationChannels(prev => prev.map(channel => 
      channel.id === channelId 
        ? { ...channel, enabled: !channel.enabled }
        : channel
    ));
  };

  const addRule = () => {
    if (!newRule.name || !newRule.condition || newRule.threshold <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please fill all required fields.",
        variant: "destructive"
      });
      return;
    }

    const rule: AlertRule = {
      id: Date.now().toString(),
      ...newRule,
      enabled: true
    };

    setAlertRules(prev => [...prev, rule]);
    setNewRule({
      name: '',
      type: 'budget',
      condition: '',
      threshold: 0,
      channels: [],
      frequency: 'immediate'
    });
    setShowAddRule(false);
    
    toast({
      title: "Alert Rule Added",
      description: "The new alert rule has been created.",
    });
  };

  const addChannel = () => {
    if (!newChannel.name || !newChannel.destination) {
      toast({
        title: "Invalid Input",
        description: "Please provide both name and destination.",
        variant: "destructive"
      });
      return;
    }

    const channel: NotificationChannel = {
      id: Date.now().toString(),
      ...newChannel,
      enabled: true
    };

    setNotificationChannels(prev => [...prev, channel]);
    setNewChannel({
      name: '',
      type: 'email',
      destination: ''
    });
    setShowAddChannel(false);
    
    toast({
      title: "Notification Channel Added",
      description: "The new notification channel has been created.",
    });
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'budget':
        return <TrendingUp className="h-4 w-4" />;
      case 'usage':
        return <Users className="h-4 w-4" />;
      case 'performance':
        return <Settings className="h-4 w-4" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'sms':
        return <Smartphone className="h-4 w-4" />;
      case 'webhook':
        return <Settings className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'destructive';
      case 'warning':
        return 'warning';
      case 'info':
        return 'default';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Usage Alerts & Notifications</h3>
          <p className="text-sm text-muted-foreground">
            Configure alerts and notification channels for monitoring
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Alert Rules
              </CardTitle>
              <Button size="sm" onClick={() => setShowAddRule(!showAddRule)}>
                Add Rule
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {showAddRule && (
                <Card className="bg-muted/50">
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Rule Name</Label>
                        <Input
                          placeholder="e.g., High Usage Alert"
                          value={newRule.name}
                          onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={newRule.type}
                          onValueChange={(value: any) => setNewRule(prev => ({ ...prev, type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="budget">Budget</SelectItem>
                            <SelectItem value="usage">Usage</SelectItem>
                            <SelectItem value="performance">Performance</SelectItem>
                            <SelectItem value="error">Error</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Condition</Label>
                        <Input
                          placeholder="e.g., daily_spend_exceeds"
                          value={newRule.condition}
                          onChange={(e) => setNewRule(prev => ({ ...prev, condition: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Threshold</Label>
                        <Input
                          type="number"
                          value={newRule.threshold}
                          onChange={(e) => setNewRule(prev => ({ ...prev, threshold: parseFloat(e.target.value) }))}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={addRule}>Add Rule</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddRule(false)}>
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-3">
                {alertRules.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getAlertIcon(rule.type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{rule.name}</span>
                          <Badge variant={rule.type as any}>{rule.type}</Badge>
                          {rule.lastTriggered && (
                            <span className="text-xs text-muted-foreground">
                              Last: {rule.lastTriggered}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {rule.condition} {rule.threshold}% - {rule.frequency}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={() => toggleRule(rule.id)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Notification Channels
              </CardTitle>
              <Button size="sm" onClick={() => setShowAddChannel(!showAddChannel)}>
                Add Channel
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {showAddChannel && (
                <Card className="bg-muted/50">
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Channel Name</Label>
                        <Input
                          placeholder="e.g., Admin Email"
                          value={newChannel.name}
                          onChange={(e) => setNewChannel(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={newChannel.type}
                          onValueChange={(value: any) => setNewChannel(prev => ({ ...prev, type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="webhook">Webhook</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Destination</Label>
                      <Input
                        placeholder={
                          newChannel.type === 'email' ? 'email@example.com' :
                          newChannel.type === 'sms' ? '+1234567890' :
                          'https://webhook.example.com'
                        }
                        value={newChannel.destination}
                        onChange={(e) => setNewChannel(prev => ({ ...prev, destination: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={addChannel}>Add Channel</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddChannel(false)}>
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-3">
                {notificationChannels.map((channel) => (
                  <div key={channel.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getChannelIcon(channel.type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{channel.name}</span>
                          <Badge variant="outline">{channel.type}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {channel.destination}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={channel.enabled}
                      onCheckedChange={() => toggleChannel(channel.id)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Recent Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentAlerts.map((alert) => (
                <div key={alert.id} className="p-3 border rounded-lg">
                  <div className="flex items-start gap-2">
                    <Badge variant={getSeverityColor(alert.severity) as any} className="mt-0.5">
                      {alert.severity}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">
                        {alert.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {alert.time}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}